/**
 * DecisionBadge — reusable truthful renderer for Manager/HR decision cells.
 *
 * Consumes the pure classifier from lib/leaveDecisionClassifier and renders
 * a badge + optional sub-label + shadcn tooltip carrying the audit trail
 * (actor name/email, timestamp, reason).
 *
 * Never defaults to "Rejected" on unknown/missing actions — that was the
 * production bug this component was built to eliminate.
 */
import React from 'react';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { TONE_TO_BADGE_CLASS } from '@/lib/leaveDecisionClassifier';

export const DecisionBadge = ({ decision, isRTL = false, testid = 'decision' }) => {
  if (!decision || decision.kind === 'none') {
    return (
      <span className="text-slate-400" data-testid={`${testid}-none`}>–</span>
    );
  }
  const badgeClass = TONE_TO_BADGE_CLASS[decision.tone] || TONE_TO_BADGE_CLASS.slate;
  const label = isRTL ? decision.label_ar : decision.label;
  const sub = isRTL ? decision.sub_ar : decision.sub;

  const IconEl =
    decision.kind === 'approved' ? CheckCircle :
    decision.kind === 'rejected' ? XCircle :
    decision.kind === 'pending' ? Clock :
    null;

  const chip = (
    <span
      className="inline-flex flex-col items-start gap-0.5 cursor-help"
      data-testid={`${testid}-${decision.kind}`}
    >
      <Badge className={cn('border', badgeClass)}>
        {IconEl && <IconEl className="h-3 w-3 me-1" />}
        {label}
      </Badge>
      {sub && (
        <span className="text-[10px] uppercase tracking-wide text-slate-500 leading-none pt-0.5">
          {sub}
        </span>
      )}
    </span>
  );

  if (!decision.tooltip) return chip;

  const t = decision.tooltip;
  const title = isRTL ? t.title_ar : t.title;
  let timeStr = null;
  if (t.timestamp) {
    try { timeStr = format(new Date(t.timestamp), 'PPp'); }
    catch { timeStr = t.timestamp; }
  }

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px] bg-slate-900 text-white">
        <div className="text-xs space-y-1">
          <div className="font-medium">{title}</div>
          {t.actor_name && (
            <div className="text-slate-300">
              {isRTL ? 'بواسطة: ' : 'By: '}
              {t.actor_name}
              {t.actor_email ? ` (${t.actor_email})` : ''}
            </div>
          )}
          {!t.actor_name && t.actor_email && (
            <div className="text-slate-300">
              {isRTL ? 'بواسطة: ' : 'By: '}
              {t.actor_email}
            </div>
          )}
          {timeStr && (
            <div className="text-slate-300">
              {isRTL ? 'الوقت: ' : 'At: '}{timeStr}
            </div>
          )}
          {t.reason && (
            <div className="text-slate-200 pt-1 border-t border-slate-700 mt-1">
              {isRTL ? 'السبب: ' : 'Reason: '}{t.reason}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default DecisionBadge;
